import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PageLoaderProps {
  message?: string;
  fullScreen?: boolean;
}

/**
 * PageLoader - Suspense fallback component for lazy-loaded pages
 * 
 * Usage:
 * ```tsx
 * <Suspense fallback={<PageLoader />}>
 *   <LazyLoadedPage />
 * </Suspense>
 * ```
 */
export function PageLoader({ message, fullScreen = true }: PageLoaderProps) {
  const { t } = useTranslation();
  
  const containerClass = fullScreen 
    ? 'fixed inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-50'
    : 'flex items-center justify-center min-h-[200px] w-full';

  return (
    <div className={containerClass}>
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          {message || t('common.loading', 'Loading...')}
        </p>
      </div>
    </div>
  );
}

/**
 * InlineLoader - For loading states within components
 */
export function InlineLoader({ message }: { message?: string }) {
  return <PageLoader message={message} fullScreen={false} />;
}

export default PageLoader;
